import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  BarChart3, Globe, Users, TrendingUp, Eye, 
  Facebook, Instagram, Youtube, MessageCircle,
  Plus, Trash2, Check, X, Copy, Share2
} from 'lucide-react';

// Register Chart.js components
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [adBanners, setAdBanners] = useState([]);
  const [adTemplates, setAdTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateAd, setShowCreateAd] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newAd, setNewAd] = useState({ title: '', description: '', platforms: [] });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, geoRes, revenueRes, conversionRes, bannersRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/analytics/geographic`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/analytics/revenue`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/analytics/conversions`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/ads/banners`, { headers }).catch(() => ({ data: { banners: [] } })),
        axios.get(`${API_URL}/api/ads/templates`).catch(() => ({ data: { templates: {} } }))
      ]);
      
      // Combine all analytics data
      setAnalytics({
        dashboard: dashboardRes.data,
        geographic: geoRes.data,
        revenue: revenueRes.data,
        conversions: conversionRes.data,
        // Format for existing charts
        visitors: {
          daily: geoRes.data.by_day || [],
          by_country: (geoRes.data.by_country || []).map(c => ({
            country: c.country_code || c.country,
            count: c.visits || c.unique_visitors || 0
          })),
          total: geoRes.data.total_visits || 0
        }
      });
      setAdBanners(bannersRes.data.banners || []);
      setAdTemplates(templatesRes.data.templates || {});
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const dailyVisitorsData = {
    labels: analytics?.geographic?.by_day?.map(d => d.date?.slice(5) || d) || [],
    datasets: [{
      label: 'Visiteurs',
      data: analytics?.geographic?.by_day?.map(d => d.visits || d.count || 0) || [],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4
    }]
  };

  const visitorsByCountryData = {
    labels: analytics?.geographic?.by_country?.slice(0, 10).map(c => c.country_code || c.country) || [],
    datasets: [{
      label: 'Visiteurs',
      data: analytics?.geographic?.by_country?.slice(0, 10).map(c => c.visits || c.unique_visitors || 0) || [],
      backgroundColor: [
        '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
      ]
    }]
  };

  const subscriptionData = {
    labels: ['Patients Gratuits', 'Patients Premium', 'Pros Gratuits', 'Pros Premium'],
    datasets: [{
      data: [
        (analytics?.users?.total_patients || 0) - (analytics?.users?.premium_patients || 0),
        analytics?.users?.premium_patients || 0,
        (analytics?.users?.total_professionals || 0) - (analytics?.users?.premium_professionals || 0),
        analytics?.users?.premium_professionals || 0
      ],
      backgroundColor: ['#94A3B8', '#3B82F6', '#CBD5E1', '#10B981']
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } }
  };

  // Ad banner functions
  const handleCreateAd = async () => {
    try {
      await axios.post(`${API_URL}/api/ads/banners`, newAd, { headers });
      toast.success('Bannière créée');
      setShowCreateAd(false);
      setNewAd({ title: '', description: '', platforms: [] });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleAd = async (id, field, value) => {
    try {
      await axios.patch(`${API_URL}/api/ads/banners/${id}?${field}=${value}`, {}, { headers });
      toast.success('Mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteAd = async (id) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;
    try {
      await axios.delete(`${API_URL}/api/ads/banners/${id}`, { headers });
      toast.success('Supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const getPlatformIcon = (platform) => {
    switch(platform) {
      case 'facebook': return <Facebook className="w-5 h-5 text-blue-600" />;
      case 'instagram': return <Instagram className="w-5 h-5 text-pink-600" />;
      case 'youtube': return <Youtube className="w-5 h-5 text-red-600" />;
      case 'tiktok': return <span className="text-lg">🎵</span>;
      case 'whatsapp': return <MessageCircle className="w-5 h-5 text-green-600" />;
      default: return <Share2 className="w-5 h-5" />;
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards - Main Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-blue-100 text-sm">Visiteurs (30j)</p>
                <p className="text-2xl font-bold">{analytics?.geographic?.total_visits || analytics?.dashboard?.today?.visits || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-green-100 text-sm">Conversion</p>
                <p className="text-2xl font-bold">{analytics?.conversions?.conversion_rates?.visit_to_registration || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-purple-100 text-sm">Utilisateurs</p>
                <p className="text-2xl font-bold">{analytics?.dashboard?.users?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-amber-100 text-sm">MRR</p>
                <p className="text-2xl font-bold">{analytics?.revenue?.recurring?.mrr || 0}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue & Conversion Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Revenus du mois</p>
              <p className="text-3xl font-bold text-green-600">{analytics?.revenue?.current_month?.revenue || 0}€</p>
              <p className="text-xs text-muted-foreground">Projection: {analytics?.revenue?.current_month?.projected || 0}€</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">ARR (Annuel)</p>
              <p className="text-3xl font-bold text-blue-600">{analytics?.revenue?.recurring?.arr || 0}€</p>
              <p className="text-xs text-muted-foreground">{analytics?.revenue?.recurring?.active_subscriptions || 0} abonnements actifs</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Taux d'abonnement</p>
              <p className="text-3xl font-bold text-purple-600">{analytics?.conversions?.conversion_rates?.registration_to_subscription || 0}%</p>
              <p className="text-xs text-muted-foreground">Inscription → Premium</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Premium Users Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Utilisateurs Premium
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{analytics?.revenue?.recurring?.premium_patients || 0}</p>
              <p className="text-sm text-muted-foreground">Patients Premium</p>
              <p className="text-xs text-blue-600">10€/mois</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{analytics?.revenue?.recurring?.premium_professionals || 0}</p>
              <p className="text-sm text-muted-foreground">Professionnels Premium</p>
              <p className="text-xs text-purple-600">200€/mois</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{analytics?.dashboard?.users?.premium || 0}</p>
              <p className="text-sm text-muted-foreground">Total Premium</p>
              <p className="text-xs text-green-600">{analytics?.dashboard?.users?.premium_rate || 0}% du total</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{analytics?.dashboard?.today?.registrations || 0}</p>
              <p className="text-sm text-muted-foreground">Inscriptions aujourd'hui</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Visiteurs Journaliers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line data={dailyVisitorsData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-green-500" />
              Visiteurs par Pays
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={visitorsByCountryData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Abonnements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <Doughnut data={subscriptionData} options={{ ...chartOptions, plugins: { legend: { display: true, position: 'bottom' } } }} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Top Villes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.visitors?.by_city?.slice(0, 5).map((city, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <span>{city.city || 'Inconnu'} ({city.country})</span>
                  <Badge>{city.count} visites</Badge>
                </div>
              ))}
              {(!analytics?.visitors?.by_city || analytics.visitors.by_city.length === 0) && (
                <p className="text-slate-500 text-center py-4">Aucune donnée</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Social Media Ads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-pink-500" />
            Publicités Réseaux Sociaux
          </CardTitle>
          <Dialog open={showCreateAd} onOpenChange={setShowCreateAd}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="w-4 h-4" /> Nouvelle Pub</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer une Publicité</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Titre</Label>
                  <Input value={newAd.title} onChange={(e) => setNewAd({...newAd, title: e.target.value})} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={newAd.description} onChange={(e) => setNewAd({...newAd, description: e.target.value})} rows={4} />
                </div>
                <div>
                  <Label>Plateformes</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {['facebook', 'instagram', 'tiktok', 'youtube', 'whatsapp'].map(p => (
                      <Button
                        key={p}
                        variant={newAd.platforms.includes(p) ? "default" : "outline"}
                        size="sm"
                        onClick={() => setNewAd({
                          ...newAd,
                          platforms: newAd.platforms.includes(p) 
                            ? newAd.platforms.filter(x => x !== p)
                            : [...newAd.platforms, p]
                        })}
                      >
                        {getPlatformIcon(p)}
                        <span className="ml-1 capitalize">{p}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateAd(false)}>Annuler</Button>
                <Button onClick={handleCreateAd}>Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="templates">
            <TabsList>
              <TabsTrigger value="templates">Templates Prêts</TabsTrigger>
              <TabsTrigger value="banners">Mes Bannières ({adBanners.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="templates" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(adTemplates).map(([platform, template]) => (
                  <Card key={platform} className="border-2 hover:border-blue-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        {getPlatformIcon(platform)}
                        <span className="font-semibold capitalize">{platform}</span>
                      </div>
                      <p className="font-medium text-sm mb-2">{template.title}</p>
                      <p className="text-xs text-slate-600 line-clamp-3">{template.description}</p>
                      <p className="text-xs text-blue-500 mt-2">{template.hashtags}</p>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full mt-3"
                        onClick={() => copyToClipboard(`${template.title}\n\n${template.description}\n\n${template.hashtags}`)}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copier
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="banners" className="mt-4">
              <div className="space-y-4">
                {adBanners.map(banner => (
                  <div key={banner.id} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{banner.title}</h4>
                          {banner.is_approved ? (
                            <Badge className="bg-green-500">Approuvé</Badge>
                          ) : (
                            <Badge variant="outline">En attente</Badge>
                          )}
                          {banner.is_active ? (
                            <Badge className="bg-blue-500">Actif</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">Inactif</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{banner.description}</p>
                        <div className="flex gap-2 mt-2">
                          {banner.platforms?.map(p => (
                            <span key={p}>{getPlatformIcon(p)}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs">Approuver</span>
                          <Switch 
                            checked={banner.is_approved} 
                            onCheckedChange={(v) => handleToggleAd(banner.id, 'is_approved', v)}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs">Activer</span>
                          <Switch 
                            checked={banner.is_active}
                            disabled={!banner.is_approved}
                            onCheckedChange={(v) => handleToggleAd(banner.id, 'is_active', v)}
                          />
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteAd(banner.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {adBanners.length === 0 && (
                  <p className="text-center text-slate-500 py-8">Aucune bannière créée</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
